import GQL from '@lib/cms/gql';
import { COMMUNITY_QUERY } from '@lib/fragments';
import { fetchAssociatedAgent, getAssociatedAgentItem } from '@lib/associatedAgent';
import communityFallbacks from '@src/data/community-fallbacks';
import Window from '@lib/window-size';
import { useAgentData } from '@lib/hooks';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useRouter } from 'next/router';

// ---------------------------------------------------------

import Accordion from '@components/accordion';
import Callout from '@components/callout';
import Container from '@layout/container';
import Form from '@components/forms';
import Image from '@components/image';
import Jumbotron from '@components/jumbotron';
import Layout from '@layout/index';
import TextBlock from '@components/text-block';
import TopicCarousel from '@components/topic-carousel';
import EnhancedContactForm from '@components/forms/enhanced';
import FormRealtorCards from '@components/forms/enhanced/realtor-cards';
import Grid from '@layout/grid';
import LifestyleCard from '@components/lifestyle-card';
import EventCard from '@components/event-card';
import TabSlider from '@components/tab-slider';
import Button from '@components/button';

// ---------------------------------------------------------

import {
  article_container,
  article_details,
  side_bar,
  summary,
  tabs,
  show_more
} from './styles.module.scss';

// ---------------------------------------------------------

export async function getServerSideProps(context) {
  const slug = context.params.slug;
  const previewEnv = context?.previewData?.environment || null;
  const preview = context.preview || false;
  const query = context?.query;
  let associatedAgent = null;

  const fallbackCommunity = communityFallbacks.find((community) => {
    return community?.fallbackSlugs?.includes(slug);
  });

  // redirect to the main community if the slug is a fallback community
  if (fallbackCommunity) {
    return {
      redirect: {
        destination: fallbackCommunity.slug,
        permanent: false
      }
    };
  }

  const data = await GQL.fetch(COMMUNITY_QUERY, { preview, slug }, preview, previewEnv);

  if (data.errors) {
    throw new Error(`Error fetching community static data: ${data.errors[0].message}`);
  }

  if (query.agentid) {
    associatedAgent = await fetchAssociatedAgent({ agentId: parseInt(query.agentid) });
  }

  // handle if no data is returned for the inserted slug
  const pageData = data?.data?.datum?.items?.[0];

  if (!pageData) {
    return {
      notFound: true
    };
  }

  // ---------------------------------------------------------

  // first show non expired, with an expiration date
  // then show by community > metro which is already ordered
  function lifestyleRecordSort(arr) {
    const withExpiration = [];
    const withoutExpiration = [];
    arr.forEach((record) => {
      record?.expirationDate ? withExpiration.push(record) : withoutExpiration.push(record);
    });
    withExpiration.sort((a, b) => new Date(b.expirationDate) - new Date(a.expirationDate));
    return [...withExpiration, ...withoutExpiration];
  }

  const getLifestyleRecords = (pageData) => {
    const currentDateTime = new Date();

    const lifestyles = [
      ...(pageData?.linkedFrom?.lifestyleRecordCollection?.items ?? []),
      ...(pageData?.metro?.linkedFrom?.lifestyleRecordCollection?.items ?? [])
    ].filter((record) => record !== null && record !== undefined);

    const lifestyleRecords = lifestyles.filter((record) => {
      return (
        record.tags.includes('Community Pages') &&
        (!record?.expirationDate || new Date(record.expirationDate) > currentDateTime)
      );
    });

    // use the sys id to ensure uniqueness when using the sorted array
    const uniqueRecords = new Set();
    const sortedRecords = lifestyleRecordSort(lifestyleRecords).filter((record) => {
      if (uniqueRecords.has(record.sys.id)) {
        return false;
      } else {
        uniqueRecords.add(record.sys.id);
        return true;
      }
    });

    return sortedRecords;
  };

  const sortEvents = (events) => {
    const currentDate = new Date();

    return events.sort((a, b) => {
      // Convert dates to Date objects for comparison
      const aStartDate = new Date(a.eventDate);
      const aExpirationDate = new Date(a.expirationDate);
      const bStartDate = new Date(b.eventDate);
      const bExpirationDate = new Date(b.expirationDate);

      // Check if events are ongoing and haven't started yet
      const aUpcomingOngoing = aStartDate > currentDate && aExpirationDate > currentDate;
      const bUpcomingOngoing = bStartDate > currentDate && bExpirationDate > currentDate;

      // Prioritize upcoming ongoing events
      if (aUpcomingOngoing && !bUpcomingOngoing) return -1;
      if (!aUpcomingOngoing && bUpcomingOngoing) return 1;

      // Sort by expirationDate
      return aExpirationDate - bExpirationDate;
    });
  };

  const getEventRecords = (pageData) => {
    const currentDateTime = new Date();

    const events = [
      ...(pageData?.linkedFrom?.eventRecordCollection?.items ?? []),
      ...(pageData?.metro?.linkedFrom?.eventRecordCollection?.items ?? [])
    ].filter((record) => record !== null && record !== undefined);

    const eventRecords = events.filter((record) => {
      return !record?.expirationDate || new Date(record.expirationDate) > currentDateTime;
    });

    // use the sys id to ensure uniqueness when using the sorted array
    const uniqueRecords = new Set();
    const sortedRecords = sortEvents(eventRecords).filter((record) => {
      if (uniqueRecords.has(record.sys.id)) {
        return false;
      } else {
        uniqueRecords.add(record.sys.id);
        return true;
      }
    });

    return sortedRecords;
  };

  // ---------------------------------------------------------

  return {
    props: {
      data: pageData || {},
      lifestyleData: getLifestyleRecords(pageData),
      eventData: getEventRecords(pageData),
      associatedAgent: getAssociatedAgentItem(associatedAgent),
      preview
    }
  };
}

const CommunityPage = ({
  data,
  preview,
  lifestyleData,
  eventData,
  associatedAgent: associatedAgentProp
}) => {
  const router = useRouter();
  const mediaIsMedium = Window().width < 1040;
  const [selectedTab, setSelectedTab] = useState('About');

  const {
    associatedAgent,
    setAssociatedAgent,
    setAssociatedAgentToSessionStorage,
    getAssociatedAgentFromSessionStorage
  } = useAgentData(associatedAgentProp);

  const carouselSlides =
    data.nearbyCommunities?.items?.map((item) => ({
      description: item?.excerpt || item?.summaryText || item?.introductionText,
      featuredImage: item?.featuredImage || {
        height: 960,
        url: 'https://images.ctfassets.net/wzzep3ntdgx4/4V3cWpoV2LctBsJYc6SnSf/4ef45ecd14a78222b508078d9fe9d345/AdobeStock_376819599.jpeg',
        width: 1440
      },
      linkLabel: item?.linkLabel || 'View Community',
      linkUrl:
        item?.__typename === 'Community'
          ? `/community/${item?.slug}`
          : `/development/${item?.slug}`,
      title: item?.title || item?.builderDevelopmentName
    })) || [];

  const contactForm = data.contactForm;

  const sectionNames = ['Transportation', 'Hospitals', 'Shopping', 'Recreation'];

  const getCommunityData = () => {
    let arr = [];

    sectionNames.map((section) => {
      const sectionId = section.toLowerCase() + 'Text';
      const sectionText = data[sectionId];

      if (sectionText) {
        arr.push({
          body: (
            <div>
              {sectionText.split('\n').map((paragraph, i) => (
                <p key={i}>{paragraph}</p>
              ))}
            </div>
          ),
          id: section,
          label: section
        });
      }
    });

    if (lifestyleData?.length > 0) {
      arr.push({
        body: (
          <div>
            <p>Check out these local highlights by area experts!</p>
            <Grid layout="2">
              {lifestyleData?.slice(0, 4).map((item, index) => (
                <LifestyleCard
                  key={index}
                  headline={item.headline}
                  authorName={item?.authorName}
                  linkLabel={item?.linkLabel}
                  linkUrl={item.linkUrl}
                  theme={item.theme}
                  featuredImage={item.featuredImage?.url}
                ></LifestyleCard>
              ))}
            </Grid>
          </div>
        ),
        id: 'Lifestyle',
        label: 'Lifestyle'
      });
    }

    return arr;
  };

  const LIMIT = 8;

  const [eventsShown, setEventsShown] = useState(LIMIT);
  const events = eventData?.slice(0, eventsShown);
  const loadMore = () => {
    setEventsShown(eventsShown + LIMIT);
  };

  useEffect(() => {
    if (router.query.tab) {
      if (router.query.tab === 'local-events' && eventData.length > 0) {
        setSelectedTab('Local Events');
      }
    }
  }, [router]);

  const handleSwitchTabs = useCallback(
    (newTab) => {
      const newQuery = {
        ...router.query,
        tab: newTab.toLowerCase().replace(' ', '-')
      };
      router.replace({ query: newQuery });
      setSelectedTab(newTab);
    },
    [router, setSelectedTab]
  );

  const routerDefaultTab = useMemo(() => {
    if (router.query.tab) {
      if (router.query.tab === 'local-events' && eventData.length > 0) {
        return 'Local Events';
      }
    }
    return 'About';
  }, [router]);

  useEffect(() => {
    // set associated agent data if it exists in session storage
    // for a case where the user comes from a page with an agentid in the query
    const agentData = getAssociatedAgentFromSessionStorage();
    if (agentData && !associatedAgentProp) {
      setAssociatedAgent(agentData);
    }
  }, [router, associatedAgentProp]);

  useEffect(() => {
    // set agent data to session storage if it was provided by the server
    if (associatedAgentProp) {
      setAssociatedAgentToSessionStorage(associatedAgentProp);
    }
  }, [associatedAgentProp]);

  return (
    <Layout
      meta={{
        description: data.metaDescription || data.introductionText,
        image: data.metaImage || data.featuredImage,
        title: data.title
      }}
      preview={preview}
    >
      {/* JUMBOTRON SECTION */}
      <Jumbotron
        body={data.serviceArea}
        ctas={
          data.viewActivePropertiesLink
            ? [
                {
                  label: 'View Active Properties',
                  theme: 'red',
                  url: data.viewActivePropertiesLink
                }
              ]
            : []
        }
        theme="centered"
        image={{
          height: '500px',
          url:
            data.headerImage?.url ||
            'https://images.ctfassets.net/wzzep3ntdgx4/B2zZsgoajAu2j4IvSpp9h/1baff99d5c5134b3fe8e014a747f2075/IMG_8039.jpg',
          width: '1440px'
        }}
        title={data.title}
      />

      {/* COMMUNITY BODY SECTIONS */}
      <Container removeBottomSpacing spacing="medium" theme="background-is-white">
        <div className={article_container}>
          <div className={article_details}>
            <div className={tabs}>
              <TabSlider
                items={[{ label: 'About' }, eventData.length > 0 && { label: 'Local Events' }]}
                theme="unified"
                defaultSelection={routerDefaultTab}
                setSelectedTabCallback={handleSwitchTabs}
              ></TabSlider>
            </div>
            {selectedTab === 'About' && (
              <>
                <div className={summary}>
                  {data.introductionText &&
                    data.introductionText
                      .split('\n')
                      .map((paragraph, i) => <p key={i}>{paragraph}</p>)}
                  {data.summaryText &&
                    data.summaryText.split('\n').map((paragraph, i) => <p key={i}>{paragraph}</p>)}
                </div>
                <Accordion
                  listItems={{ items: getCommunityData() }}
                  openAccordionId={['Lifestyle']}
                ></Accordion>
              </>
            )}
            {eventData.length > 0 && selectedTab === 'Local Events' && (
              <div>
                <p>Stay up to date with local events!</p>
                <Grid layout="1">
                  {events.map((item, index) => (
                    <EventCard
                      key={index}
                      headline={item.headline}
                      authorName={item?.authorName}
                      linkLabel={item?.linkLabel}
                      linkUrl={item.linkUrl}
                      theme={item.theme}
                      featuredImage={item.featuredImage?.url}
                      schedule={item.schedule}
                      eventDate={item.eventDate}
                      expirationDate={item.expirationDate}
                      eventDetails={item.eventDetails}
                    ></EventCard>
                  ))}
                </Grid>
                {eventData.length > LIMIT && (
                  <div className={show_more}>
                    {eventsShown < eventData.length - 1 && (
                      <Button label="Show More" onClick={loadMore}></Button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {(data.sidebar || associatedAgent) && (
            <div className={side_bar}>
              {data.sidebar?.__typename && (
                <Callout {...data.sidebar} placement="sidebar" theme="keyline" />
              )}
              {associatedAgent && (
                <EnhancedContactForm
                  primaryAgent={associatedAgent}
                  sticky={!mediaIsMedium}
                  communityName={data.title}
                  communityState={data.slug.slice(0, 2).toUpperCase()}
                  endpoint={'community information request for agent card'}
                  agentId={associatedAgent.agentId}
                >
                  <FormRealtorCards primaryContact={associatedAgent} />
                </EnhancedContactForm>
              )}
            </div>
          )}
        </div>
      </Container>

      {/* EDUCATION SECTION */}
      {data.schools?.items?.map((school, idx) => {
        return (
          <Container key={`school-${idx}`} layout="half" theme="background-is-white" width="large">
            <TextBlock
              content={school.description}
              link={{
                aria: `Learn more about ${data.serviceArea} Schools`,
                icon: true,
                label: `Explore ${data.serviceArea} Schools`,
                url: school.slug || '#'
              }}
              title="Education"
            />
            <Image
              image={{
                alt: school.image?.alt,
                height: 310,
                src: school.image?.url
              }}
            />
          </Container>
        );
      })}

      {/* NEARBY COMMUNITIES SECTION */}
      {data.nearbyCommunities?.items?.length > 0 && (
        <Container layout="single" theme="background-is-gray">
          <TopicCarousel
            carouselHeader="Explore Nearby Communities"
            slidesCollection={{
              cards: carouselSlides
            }}
            theme="full-width"
          />
        </Container>
      )}

      {/* METRO ACCORDION SECTION */}
      {data.metro?.accordion?.listItems?.items?.length > 0 && (
        <Container
          header={data.metro?.name}
          padding="large"
          width="medium"
          textAlignment="center"
          theme="background-is-white"
        >
          <Accordion theme="red" listItems={data.metro?.accordion?.listItems} />
        </Container>
      )}

      {/* CONTACT FORM SECTION */}
      {contactForm && (
        <Container>
          <Form {...contactForm} />
        </Container>
      )}
    </Layout>
  );
};

export default CommunityPage;
